import type { SelectionCategoryConfig, SelectionOptionConfig } from '@/types/firestore';
import {
  FLOORING_OPTIONS,
  WALL_OPTIONS,
  CABINET_OPTIONS,
  COUNTERTOP_OPTIONS,
  LIGHTING_OPTIONS,
  FIXTURE_OPTIONS,
  VANITY_OPTIONS,
  SHOWER_OPTIONS,
  DECK_MATERIAL_OPTIONS,
  RAILING_OPTIONS,
  PATIO_SURFACE_OPTIONS,
  BORDER_OPTIONS,
  PERGOLA_STRUCTURE_OPTIONS,
  CANOPY_OPTIONS,
  POOL_DECKING_OPTIONS,
  POOL_FENCING_OPTIONS,
  GAZEBO_STRUCTURE_OPTIONS,
  SCREENING_OPTIONS,
  OUTDOOR_COUNTERTOP_OPTIONS,
  APPLIANCE_OPTIONS,
  FIRE_TYPE_OPTIONS,
  SEATING_OPTIONS,
  FENCE_MATERIAL_OPTIONS,
  FENCE_HEIGHT_OPTIONS,
  GATE_OPTIONS,
  HARDSCAPE_OPTIONS,
  PLANTER_OPTIONS,
  PATHWAY_LIGHTING_OPTIONS,
  type SelectionCategory,
} from './roomOptions';

/**
 * Extended definition for selection categories with metadata for search/filter
 */
export interface SelectionCategoryDefinition {
  id: string;
  label: string;
  description: string;
  allowColor?: boolean;
  defaultOptions: SelectionOptionConfig[];
  tags: string[]; // For search/filter: ['outdoor', 'material', 'flooring']
}

/**
 * Convert SelectionCategory to SelectionCategoryDefinition with tags
 */
function fromSelectionCategory(
  category: SelectionCategory,
  description: string,
  tags: string[]
): SelectionCategoryDefinition {
  return {
    id: category.id,
    label: category.label,
    description,
    allowColor: category.allowColor,
    defaultOptions: category.options.map((opt) => ({
      id: opt.id,
      label: opt.label,
      description: opt.description,
    })),
    tags,
  };
}

/**
 * Full library of all available selection categories organized with metadata
 */
export const SELECTION_CATEGORY_LIBRARY: SelectionCategoryDefinition[] = [
  // ============================================
  // GENERAL CATEGORIES (work across many areas)
  // ============================================
  fromSelectionCategory(
    FLOORING_OPTIONS,
    'Floor covering materials and finishes',
    ['general', 'flooring', 'material', 'interior']
  ),
  fromSelectionCategory(
    WALL_OPTIONS,
    'Wall treatments and finishes',
    ['general', 'walls', 'material', 'interior']
  ),
  fromSelectionCategory(
    LIGHTING_OPTIONS,
    'Lighting fixtures and design',
    ['general', 'lighting', 'electrical', 'interior', 'outdoor']
  ),
  fromSelectionCategory(
    CABINET_OPTIONS,
    'Cabinet renovation options',
    ['kitchen', 'bathroom', 'cabinets', 'storage', 'interior']
  ),
  fromSelectionCategory(
    COUNTERTOP_OPTIONS,
    'Countertop surface materials',
    ['kitchen', 'bathroom', 'countertops', 'material', 'interior']
  ),

  // ============================================
  // BATHROOM-SPECIFIC
  // ============================================
  fromSelectionCategory(
    FIXTURE_OPTIONS,
    'Plumbing fixtures and hardware',
    ['bathroom', 'fixtures', 'plumbing']
  ),
  fromSelectionCategory(
    VANITY_OPTIONS,
    'Bathroom vanity options',
    ['bathroom', 'vanity', 'storage']
  ),
  fromSelectionCategory(
    SHOWER_OPTIONS,
    'Shower and tub configurations',
    ['bathroom', 'shower', 'tub', 'plumbing']
  ),

  // ============================================
  // OUTDOOR - DECK
  // ============================================
  fromSelectionCategory(
    DECK_MATERIAL_OPTIONS,
    'Decking surface materials',
    ['outdoor', 'deck', 'material', 'flooring']
  ),
  fromSelectionCategory(
    RAILING_OPTIONS,
    'Deck and patio railing styles',
    ['outdoor', 'deck', 'patio', 'railing', 'safety']
  ),

  // ============================================
  // OUTDOOR - PATIO
  // ============================================
  fromSelectionCategory(
    PATIO_SURFACE_OPTIONS,
    'Patio surface materials',
    ['outdoor', 'patio', 'material', 'flooring', 'hardscape']
  ),
  fromSelectionCategory(
    BORDER_OPTIONS,
    'Patio border and edge treatments',
    ['outdoor', 'patio', 'border', 'hardscape']
  ),

  // ============================================
  // OUTDOOR - STRUCTURES
  // ============================================
  fromSelectionCategory(
    PERGOLA_STRUCTURE_OPTIONS,
    'Pergola construction materials',
    ['outdoor', 'pergola', 'structure', 'material']
  ),
  fromSelectionCategory(
    CANOPY_OPTIONS,
    'Pergola shade and canopy options',
    ['outdoor', 'pergola', 'shade', 'canopy']
  ),
  fromSelectionCategory(
    GAZEBO_STRUCTURE_OPTIONS,
    'Gazebo construction materials',
    ['outdoor', 'gazebo', 'structure', 'material']
  ),
  fromSelectionCategory(
    SCREENING_OPTIONS,
    'Gazebo screening and enclosure options',
    ['outdoor', 'gazebo', 'screening', 'enclosure']
  ),

  // ============================================
  // OUTDOOR - POOL
  // ============================================
  fromSelectionCategory(
    POOL_DECKING_OPTIONS,
    'Pool deck and coping materials',
    ['outdoor', 'pool', 'decking', 'material']
  ),
  fromSelectionCategory(
    POOL_FENCING_OPTIONS,
    'Pool safety fencing options',
    ['outdoor', 'pool', 'fencing', 'safety']
  ),

  // ============================================
  // OUTDOOR - KITCHEN
  // ============================================
  fromSelectionCategory(
    OUTDOOR_COUNTERTOP_OPTIONS,
    'Outdoor kitchen countertop materials',
    ['outdoor', 'kitchen', 'countertop', 'material']
  ),
  fromSelectionCategory(
    APPLIANCE_OPTIONS,
    'Outdoor kitchen appliance packages',
    ['outdoor', 'kitchen', 'appliances']
  ),

  // ============================================
  // OUTDOOR - FIRE PIT
  // ============================================
  fromSelectionCategory(
    FIRE_TYPE_OPTIONS,
    'Fire pit fuel and design types',
    ['outdoor', 'fire_pit', 'fire', 'type']
  ),
  fromSelectionCategory(
    SEATING_OPTIONS,
    'Fire pit seating arrangements',
    ['outdoor', 'fire_pit', 'seating', 'furniture']
  ),

  // ============================================
  // OUTDOOR - FENCE
  // ============================================
  fromSelectionCategory(
    FENCE_MATERIAL_OPTIONS,
    'Fence construction materials',
    ['outdoor', 'fence', 'material', 'privacy']
  ),
  fromSelectionCategory(
    FENCE_HEIGHT_OPTIONS,
    'Fence height options',
    ['outdoor', 'fence', 'height', 'privacy']
  ),
  fromSelectionCategory(
    GATE_OPTIONS,
    'Gate styles and configurations',
    ['outdoor', 'fence', 'gate', 'access']
  ),

  // ============================================
  // OUTDOOR - LANDSCAPE
  // ============================================
  fromSelectionCategory(
    HARDSCAPE_OPTIONS,
    'Hardscape materials for pathways and patios',
    ['outdoor', 'landscape', 'hardscape', 'material']
  ),
  fromSelectionCategory(
    PLANTER_OPTIONS,
    'Planter and garden bed styles',
    ['outdoor', 'landscape', 'planter', 'garden']
  ),
  fromSelectionCategory(
    PATHWAY_LIGHTING_OPTIONS,
    'Landscape and pathway lighting',
    ['outdoor', 'landscape', 'lighting', 'pathway']
  ),
];

/**
 * Get all unique tags from the category library
 */
export function getAllTags(): string[] {
  const tagSet = new Set<string>();
  SELECTION_CATEGORY_LIBRARY.forEach((cat) => {
    cat.tags.forEach((tag) => tagSet.add(tag));
  });
  return Array.from(tagSet).sort();
}

/**
 * Search/filter categories by query string and optional tags
 */
export function searchCategories(
  query: string,
  filterTags?: string[]
): SelectionCategoryDefinition[] {
  const lowerQuery = query.toLowerCase().trim();

  return SELECTION_CATEGORY_LIBRARY.filter((cat) => {
    // Tag filter
    if (filterTags && filterTags.length > 0) {
      const hasMatchingTag = filterTags.some((tag) => cat.tags.includes(tag));
      if (!hasMatchingTag) return false;
    }

    // Empty query matches all (after tag filter)
    if (!lowerQuery) return true;

    // Search in label, description, and tags
    return (
      cat.label.toLowerCase().includes(lowerQuery) ||
      cat.description.toLowerCase().includes(lowerQuery) ||
      cat.tags.some((tag) => tag.toLowerCase().includes(lowerQuery))
    );
  });
}

/**
 * Get categories grouped by primary tag
 */
export function getCategoriesByTag(): Record<string, SelectionCategoryDefinition[]> {
  const groups: Record<string, SelectionCategoryDefinition[]> = {};

  SELECTION_CATEGORY_LIBRARY.forEach((cat) => {
    // Use first tag as primary group
    const primaryTag = cat.tags[0] || 'other';
    if (!groups[primaryTag]) {
      groups[primaryTag] = [];
    }
    groups[primaryTag].push(cat);
  });

  return groups;
}

/**
 * Get a category definition by ID
 */
export function getCategoryById(categoryId: string): SelectionCategoryDefinition | undefined {
  return SELECTION_CATEGORY_LIBRARY.find((cat) => cat.id === categoryId);
}

/**
 * Convert SelectionCategoryDefinition to SelectionCategoryConfig for saving
 */
export function toSelectionCategoryConfig(
  definition: SelectionCategoryDefinition
): SelectionCategoryConfig {
  return {
    id: definition.id,
    label: definition.label,
    allowColor: definition.allowColor,
    options: definition.defaultOptions,
  };
}

/**
 * Create a new empty category definition
 */
export function createEmptyCategory(id: string, label: string): SelectionCategoryDefinition {
  return {
    id,
    label,
    description: 'Custom selection category',
    allowColor: false,
    defaultOptions: [],
    tags: ['custom'],
  };
}
